import { InjectionToken, computed, inject } from '@angular/core';
import { Result, UseCaseError, tapUseCaseResponse } from '@causality/core';
import { logger } from '@fmnts/common/log';
import { LOCAL_STORAGE } from '@fmnts/common/storage';
import { SelfCheckout } from '@formunauts/self-checkout/domain';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import { pipe, switchMap } from 'rxjs';
import { SelfCheckoutUseCases } from './self-checkout.use-case';

export interface AuthFormStoreState {
  hash: SelfCheckout.Hash;
  status: SelfCheckout.FlowStatus;
  authFormData: O.Option<SelfCheckout.AuthenticationData>;
  authFormResult: O.Option<
    Result.Result<
      SelfCheckout.AuthenticationForm,
      SelfCheckout.SelfCheckoutError
    >
  >;
  checkoutData: O.Option<SelfCheckout.PreparedSelfCheckoutFormPayload>;
  checkoutFormResult: O.Option<
    Result.Result<SelfCheckout.SelfCheckoutForm, SelfCheckout.SelfCheckoutError>
  >;
  checkoutDefaultResponseResult: O.Option<
    Result.Result<
      SelfCheckout.SelfCheckoutResponseDefault,
      SelfCheckout.SelfCheckoutError
    >
  >;
  checkoutTamaroResponseResult: O.Option<
    Result.Result<
      SelfCheckout.SelfCheckoutResponseTamaro,
      SelfCheckout.SelfCheckoutError
    >
  >;
  unsubscribeFormResult: O.Option<
    Result.Result<SelfCheckout.UnsubscribeForm, SelfCheckout.SelfCheckoutError>
  >;
  unsubscribeResponseResult: O.Option<
    Result.Result<unknown, SelfCheckout.SelfCheckoutError>
  >;
}

export const SELF_CHECKOUT_HASH = new InjectionToken<SelfCheckout.Hash>('');

export const SelfCheckoutStreamStore = signalStore(
  withState<AuthFormStoreState>((hash = inject(SELF_CHECKOUT_HASH)) => ({
    hash,
    status: SelfCheckout.FlowStatus.Init,
    authFormData: O.none(),
    authFormResult: O.none(),
    checkoutData: O.none(),
    checkoutFormResult: O.none(),
    checkoutDefaultResponseResult: O.none(),
    checkoutTamaroResponseResult: O.none(),
    unsubscribeFormResult: O.none(),
    unsubscribeResponseResult: O.none(),
  })),
  withComputed(
    ({
      authFormResult,
      checkoutFormResult,
      checkoutDefaultResponseResult,
      checkoutTamaroResponseResult,
      unsubscribeFormResult,
      unsubscribeResponseResult,
    }) => ({
      authForm: computed(() => O.flatMap(authFormResult(), Result.getSuccess)),
      authFormError: computed(() =>
        O.flatMap(authFormResult(), Result.getFailure),
      ),
      selfCheckoutForm: computed(() =>
        O.flatMap(checkoutFormResult(), Result.getSuccess),
      ),
      selfCheckoutFormError: computed(() =>
        O.flatMap(checkoutFormResult(), Result.getFailure),
      ),
      checkoutDefaultResponse: computed(() =>
        O.flatMap(checkoutDefaultResponseResult(), Result.getSuccess),
      ),
      checkoutDefaultResponseError: computed(() =>
        O.flatMap(checkoutDefaultResponseResult(), Result.getFailure),
      ),
      checkoutTamaroResponse: computed(() =>
        O.flatMap(checkoutTamaroResponseResult(), Result.getSuccess),
      ),
      checkoutTamaroResponseError: computed(() =>
        O.flatMap(checkoutTamaroResponseResult(), Result.getFailure),
      ),

      unsubscribeForm: computed(() =>
        O.flatMap(unsubscribeFormResult(), Result.getSuccess),
      ),
      unsubscribeFormError: computed(() =>
        O.flatMap(unsubscribeFormResult(), Result.getFailure),
      ),
      unsubscribedResponse: computed(() =>
        O.flatMap(unsubscribeResponseResult(), Result.getSuccess),
      ),
      unsubscribeResponseError: computed(() =>
        O.flatMap(unsubscribeResponseResult(), Result.getFailure),
      ),
      paymentMethod: computed(() => {}),
    }),
  ),
  withMethods(
    (
      store,
      uc = inject(SelfCheckoutUseCases),
      log = logger({ name: 'SelfCheckoutStreamStore' }),
      ls = inject(LOCAL_STORAGE),
    ) => ({
      setAuthFormData(authData: SelfCheckout.AuthenticationData): void {
        patchState(store, { authFormData: O.some(authData) });
      },
      setCheckoutData(
        checkoutOutData: SelfCheckout.PreparedSelfCheckoutFormPayload,
      ): void {
        patchState(store, { checkoutData: O.some(checkoutOutData) });
      },
      retriveCheckoutFormStorage(
        hash: SelfCheckout.Hash,
      ): O.Option<SelfCheckout.SelfCheckoutForm> {
        return Fn.pipe(
          ls.getItem(_storeKey(hash, 'confirm')),
          O.fromNullable,
          O.map(Fn.flow(JSON.parse, SelfCheckout.SelfCheckoutForm)),
        );
      },
      saveCheckoutFormStorage(
        hash: SelfCheckout.Hash,
        form: SelfCheckout.SelfCheckoutForm,
      ): void {
        ls.setItem(_storeKey(hash, 'confirm'), JSON.stringify(form));
      },
      clearCheckoutFormStorage(hash: SelfCheckout.Hash): void {
        ls.removeItem(_storeKey(hash, 'confirm'));
      },
      getAuthForm: rxMethod<SelfCheckout.Hash>(
        pipe(
          switchMap((hash) =>
            uc.getAuthenticationForm(hash).pipe(
              tapUseCaseResponse({
                next: (authFormResult) => {
                  log.debug('get auth form');
                  const error = Result.getFailure(authFormResult);
                  const status = Fn.pipe(error, _selfCheckoutToFlowStatus);

                  if (status === SelfCheckout.FlowStatus.Error) {
                    log.debug('SCO fetching form failed:', O.getOrNull(error));
                  }
                  patchState(store, {
                    authFormResult: O.some(authFormResult),
                    status,
                  });
                },
                error: (error: UseCaseError) => {
                  patchState(store, {
                    status: SelfCheckout.FlowStatus.Error,
                  });
                  log.error(' Unexpected Use case error', error);
                },
              }),
            ),
          ),
        ),
      ),
      authenticate: rxMethod<{
        preparedData: SelfCheckout.AuthenticationData;
        hash: SelfCheckout.Hash;
      }>(
        pipe(
          switchMap(({ preparedData, hash }) =>
            uc.authenticate(preparedData).pipe(
              tapUseCaseResponse({
                next: (selfChekoutFormResult) => {
                  const maybeSelfCheckoutForm = O.getOrNull(
                    Result.getSuccess(selfChekoutFormResult),
                  );

                  const error = Result.getFailure(selfChekoutFormResult);
                  const status = Fn.pipe(error, _selfCheckoutToFlowStatus);

                  if (status === SelfCheckout.FlowStatus.Error) {
                    log.error('SCO authentication failed:', O.getOrNull(error));
                  }

                  patchState(store, {
                    checkoutFormResult: O.some(selfChekoutFormResult),
                    status,
                  });
                  ls.setItem(
                    _storeKey(hash, 'confirm'),
                    JSON.stringify(maybeSelfCheckoutForm),
                  );
                },
                error: (error: UseCaseError) => {
                  patchState(store, {
                    status: SelfCheckout.FlowStatus.Error,
                  });
                  log.error(' Unexpected Use case error', error);
                },
              }),
            ),
          ),
        ),
      ),
      checkoutTamaro: rxMethod<{
        preparedData: SelfCheckout.PreparedSelfCheckoutFormPayload;
        hash: SelfCheckout.Hash;
      }>(
        pipe(
          switchMap(({ preparedData, hash }) =>
            uc.checkoutTamaro(preparedData).pipe(
              tapUseCaseResponse({
                next: (value) => {
                  const error = Result.getFailure(value);
                  const status = Fn.pipe(error, _selfCheckoutToFlowStatus);

                  if (status === SelfCheckout.FlowStatus.Error) {
                    log.error(
                      'SCO checkout tamaro failed:',
                      O.getOrNull(error),
                    );
                  }
                  patchState(store, {
                    checkoutTamaroResponseResult: O.some(value),
                    status,
                  });
                },
                error: (error: UseCaseError) => {
                  patchState(store, {
                    status: SelfCheckout.FlowStatus.Error,
                  });
                  log.error(' Unexpected Use case error', error);
                },
              }),
            ),
          ),
        ),
      ),
      checkout: rxMethod<{
        preparedData: SelfCheckout.PreparedSelfCheckoutFormPayload;
        hash: SelfCheckout.Hash;
      }>(
        pipe(
          switchMap(({ preparedData, hash }) =>
            uc.checkoutDefault(preparedData).pipe(
              tapUseCaseResponse({
                next: (checkoutResponse) => {
                  log.debug('checkout');

                  const error = Result.getFailure(checkoutResponse);
                  const status = Fn.pipe(error, _selfCheckoutToFlowStatus);

                  if (status === SelfCheckout.FlowStatus.Error) {
                    log.error(
                      'SCO checkout default failed:',
                      O.getOrNull(error),
                    );
                  }
                  patchState(store, {
                    checkoutDefaultResponseResult: O.some(checkoutResponse),
                    status,
                  });
                },
                error: (error: UseCaseError) => {
                  patchState(store, {
                    status: SelfCheckout.FlowStatus.Error,
                  });
                  log.error(' Unexpected Use case error', error);
                },
              }),
            ),
          ),
        ),
      ),
      getUnsubscribeForm: rxMethod<SelfCheckout.Hash>(
        pipe(
          switchMap((hash) =>
            uc.getUnsubscribeForm(hash).pipe(
              tapUseCaseResponse({
                next: (unsubscribeFormResult) => {
                  patchState(store, {
                    unsubscribeFormResult: O.some(unsubscribeFormResult),
                  });
                },
                error: (error: UseCaseError) => {
                  patchState(store, {
                    status: SelfCheckout.FlowStatus.Error,
                  });
                  log.error(' Unexpected Use case error', error);
                },
              }),
            ),
          ),
        ),
      ),
      unsubscribe: rxMethod<SelfCheckout.PreparedUnsubscribeFormPayload>(
        pipe(
          switchMap((preparedData) =>
            uc.unsubscribe(preparedData).pipe(
              tapUseCaseResponse({
                next: (result) => {
                  patchState(store, {
                    unsubscribeResponseResult: O.some(result),
                  });
                },
                error: (error: UseCaseError) => {
                  patchState(store, {
                    status: SelfCheckout.FlowStatus.Error,
                  });
                  log.error(' Unexpected Use case error', error);
                },
              }),
            ),
          ),
        ),
      ),
    }),
  ),
);

const _selfCheckoutToFlowStatus = Fn.flow(
  O.map((e: SelfCheckout.SelfCheckoutError) =>
    e instanceof SelfCheckout.SelfCheckoutCompletedError
      ? SelfCheckout.FlowStatus.Done
      : SelfCheckout.FlowStatus.Error,
  ),
  O.getOrElse(() => SelfCheckout.FlowStatus.InProgress),
);

type SelfCheckoutStore = Record<
  string,
  {
    confirm?: SelfCheckout.SelfCheckoutForm;
  }
>;

function _storeKey(): string;
function _storeKey<K1 extends keyof SelfCheckoutStore>(hash: K1): string;
function _storeKey<
  K1 extends keyof SelfCheckoutStore,
  K2 extends keyof SelfCheckoutStore[K1],
>(hash: K1, k2: K2): string;
function _storeKey(...path: string[]): string {
  return ['checkout', ...path].join('.');
}
